/**
 * @fileoverview Syndicator Showdown — Public Invite Landing Page
 * @module pages/ShareInvitePage
 *
 * Public route: /share/invite/:referralCode
 * No authentication required. Tracks the click, caches the referral code so
 * it can be attached to a new user's account at sign-up, and shows a simple
 * "Join the Showdown" pitch.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  storeReferralCode, trackReferralClick, fetchReferralOwner,
} from '../services/referralService';
import './ShareInvitePage.css';

export function ShareInvitePage() {
  const { referralCode } = useParams();
  const [owner, setOwner]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (!referralCode) { setLoading(false); return; }

    storeReferralCode(referralCode);
    trackReferralClick(referralCode);

    let cancelled = false;
    fetchReferralOwner(referralCode)
      .then((result) => { if (!cancelled) setOwner(result); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [referralCode]);

  const inviterName = owner?.ownerDisplayName;

  return (
    <div className="shp-root">
      <div className="shp-bg" />
      <div className="shp-bg-overlay" />

      <header className="shp-header">
        <Link to="/" className="shp-logo-link">
          <img src="/logo.png" alt="Syndicator Showdown" className="shp-logo" />
        </Link>
        <div className="shp-header-actions">
          <Link to="/signin" className="shp-btn-ghost">Login</Link>
        </div>
      </header>

      <main className="shp-content">
        <div className="shp-card">
          <span className="shp-eyebrow">🐎 YOU'VE BEEN INVITED</span>

          {loading ? (
            <div className="shp-spinner-wrap"><div className="ss-spinner" /></div>
          ) : (
            <>
              <h1 className="shp-title">
                {inviterName
                  ? <>{inviterName} has challenged you to join the Showdown</>
                  : <>Join the Showdown</>}
              </h1>
              <p className="shp-subtitle">
                Build your own racing stable, own equity in real-world runners, and compete
                for the top of the leaderboard — free to play, no real-money wagering.
              </p>

              <div className="shp-actions">
                <Link to="/signin?mode=signup" className="shp-btn-primary">
                  Join the Showdown →
                </Link>
                <Link to="/" className="shp-btn-secondary">
                  Learn More
                </Link>
              </div>
            </>
          )}
        </div>

        <div className="shp-features">
          <div className="shp-feature">
            <span className="shp-feature-icon">🏇</span>
            <p>Draft real racehorses into your own virtual stable</p>
          </div>
          <div className="shp-feature">
            <span className="shp-feature-icon">📈</span>
            <p>Buy equity, track prices, and manage your bankroll</p>
          </div>
          <div className="shp-feature">
            <span className="shp-feature-icon">🏆</span>
            <p>Climb the leaderboard and win virtual prizes</p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default ShareInvitePage;
